/**
 * Solo Mode "Football Islands" — Map screen.
 *
 * Renders 100 numbered islands in a serpentine vertical layout. The map
 * is an SVG that draws a smooth curving path connecting consecutive
 * islands left → right → left → right…  Islands are visually:
 *   - cleared:        green fill + check icon
 *   - next-to-play:   accent (FootGuesser green) + pulsing ring
 *   - locked:         zinc fill + small lock icon
 *
 * Top-bar carries the SoloLivesBadge (lives + regen countdown). At
 * mount we auto-scroll to the next-to-play island so users see context.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import SoloLivesBadge from "../components/SoloLivesBadge";

const TOTAL = 100;
const ROW_GAP = 110; // vertical px between rows
const ISLE = 64;     // island diameter px
const SIDE_X = [80, 200, 320, 440]; // 4 distinct x-positions for the serpentine
const COLS = SIDE_X.length;

function islandPos(i) {
  // i: 0-indexed level number (level 1 → i=0)
  const row = Math.floor(i / COLS);
  const colInRow = i % COLS;
  const col = row % 2 === 0 ? colInRow : COLS - 1 - colInRow;
  return { x: SIDE_X[col], y: 60 + row * ROW_GAP };
}

function pathBetween(a, b) {
  // Smooth cubic-bezier between two island centers
  const midY = (a.y + b.y) / 2;
  return `M ${a.x} ${a.y} C ${a.x} ${midY} ${b.x} ${midY} ${b.x} ${b.y}`;
}

export default function SoloMap() {
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState(() => location.state?.progress || null);
  const [livesData, setLivesData] = useState(() => location.state?.lives || null);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const nextIslandRef = useRef(null);

  // Always re-fetch on mount (route entry) AND whenever the window/tab regains
  // focus. This guarantees the map reflects the latest server state after a
  // user clears a level on /solo/level/:n and navigates back.
  const refetch = useCallback(() => {
    let cancelled = false;
    Promise.all([api.get("/solo/progress"), api.get("/solo/lives")])
      .then(([pRes, lRes]) => {
        if (cancelled) return;
        setProgress(pRes.data);
        setLivesData(lRes.data);
        setError(null);
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || "failed"));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const cleanup = refetch();
    const onFocus = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cleanup();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    // `location.key` changes on every navigation to this route (incl. back/forward),
    // so this effect re-runs and we re-fetch even if React re-uses the same component.
  }, [refetch, location.key]);

  // Auto-scroll to the next-to-play island once we have data
  useEffect(() => {
    if (!progress || !nextIslandRef.current || !scrollRef.current) return;
    const node = nextIslandRef.current;
    const wrap = scrollRef.current;
    // Center the node vertically in the scroll viewport
    const target = node.offsetTop - wrap.clientHeight / 2 + ISLE / 2;
    wrap.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
  }, [progress]);

  const cleared = useMemo(() => new Set(progress?.cleared_levels || []), [progress]);
  const nextLevel = progress?.max_unlocked_level || 1;

  function clickIsland(level) {
    if (level > nextLevel) return; // locked
    if ((livesData?.lives ?? 0) <= 0 && level === nextLevel) {
      toast.error("Out of lives — wait for regen", { id: "no-lives" });
      return;
    }
    navigate(`/solo/level/${level}`);
  }

  if (error) {
    return (
      <div data-testid="solo-map-error" className="min-h-screen bg-[var(--bg)] text-zinc-400 flex items-center justify-center">
        Failed to load Solo: {String(error)}
      </div>
    );
  }
  if (!progress || !livesData) {
    return (
      <div data-testid="solo-map-loading" className="min-h-screen bg-[var(--bg)] text-zinc-400 flex items-center justify-center">
        <div className="text-xs uppercase tracking-[0.3em]">loading map…</div>
      </div>
    );
  }

  // SVG dimensions
  const rows = Math.ceil(TOTAL / COLS);
  const svgH = 60 + rows * ROW_GAP;
  const svgW = 480;

  // Build connector paths
  const positions = Array.from({ length: TOTAL }, (_, i) => islandPos(i));
  const connectors = [];
  for (let i = 0; i < TOTAL - 1; i++) {
    connectors.push(
      <path
        key={i}
        d={pathBetween(positions[i], positions[i + 1])}
        stroke={i + 1 < nextLevel ? "rgba(0,255,135,0.45)" : "rgba(255,255,255,0.08)"}
        strokeWidth="4"
        strokeDasharray={i + 1 < nextLevel ? "0" : "6 8"}
        fill="none"
        strokeLinecap="round"
      />
    );
  }

  return (
    <div
      data-testid="solo-map-page"
      className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur bg-black/60 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link
            to="/dashboard"
            data-testid="solo-back-to-dashboard"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 transition text-sm"
          >
            <ArrowLeft size={14} /> Dashboard
          </Link>
          <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">
            Football Islands
          </div>
          <SoloLivesBadge
            lives={livesData.lives}
            nextRegenInSeconds={livesData.next_regen_in_seconds}
          />
        </div>
        <div className="max-w-3xl mx-auto px-5 pb-3 text-xs text-zinc-500 flex items-center justify-between">
          <span data-testid="solo-progress-text">
            Level {nextLevel - 1} cleared · Next: <span className="text-[var(--accent)] font-bold">#{nextLevel}</span>
          </span>
          <span>{progress.total_levels} islands</span>
        </div>
      </header>

      {/* Map */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-3 py-6 relative" style={{ height: svgH + 80 }}>
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            width="100%"
            height={svgH}
            preserveAspectRatio="xMidYMin meet"
            className="absolute inset-0"
          >
            {connectors}
          </svg>
          {positions.map((pos, i) => {
            const level = i + 1;
            const isCleared = cleared.has(level);
            const isNext = level === nextLevel;
            const isLocked = level > nextLevel;
            const left = `${(pos.x / svgW) * 100}%`;
            const isThisRef = isNext ? nextIslandRef : null;
            return (
              <motion.button
                key={level}
                ref={isThisRef}
                data-testid={`solo-island-${level}`}
                onClick={() => clickIsland(level)}
                disabled={isLocked}
                style={{ left, top: pos.y - ISLE / 2 }}
                className={[
                  "absolute -translate-x-1/2 rounded-full flex items-center justify-center font-black text-sm select-none",
                  "shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]",
                  isCleared && "bg-emerald-500 text-black border-2 border-emerald-300",
                  isNext && "bg-[var(--accent)] text-black border-2 border-white/80",
                  isLocked && "bg-zinc-800 text-zinc-500 border-2 border-white/5 cursor-not-allowed",
                ].filter(Boolean).join(" ")}
                whileHover={!isLocked ? { scale: 1.08 } : undefined}
                whileTap={!isLocked ? { scale: 0.96 } : undefined}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: Math.min(0.3, i * 0.005), type: "spring", stiffness: 200, damping: 18 }}
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center">
                  {isCleared ? (
                    <Check size={26} strokeWidth={3} />
                  ) : isLocked ? (
                    <Lock size={20} strokeWidth={2.5} />
                  ) : (
                    <span className="text-base">{level}</span>
                  )}
                </div>
                {isNext && (
                  <motion.span
                    className="absolute -inset-2 rounded-full border-2 border-[var(--accent)] pointer-events-none"
                    animate={{ scale: [1, 1.25, 1], opacity: [0.8, 0, 0.8] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                    aria-hidden
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
